import { Component, EventEmitter, Inject, InjectionToken, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { LegacyPricingPlans, PlanActionType, PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { BadgeInfo, PlanStateService } from 'src/app/services/billing/plan-state.service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { TrialOfferService } from 'src/app/services/trial-offer/trial-offer.service';
import { formatPriceForTooltip, normalizePrice } from 'src/app/util/price-formatter.util';
import { SubscriptionStatus } from '../subscription-status-badge/subscription-status-badge.component';

export type PlanCardClickEvent = {
  plan: ConcretePlan;
  actionType: PlanActionType;
  isV2?: boolean;
};

@UntilDestroy()
@Component({
  selector: 'app-plan-card',
  templateUrl: './plan-card.component.html',
  styleUrls: ['./plan-card.component.scss'],
})
export class PlanCardComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  isFuturePlan = false;

  @Input()
  currentPlan: ConcretePlan;

  @Input()
  plan: ConcretePlan;

  @Input()
  get payRecurrence(): PricePeriodicity {
    return this.rawPayRecurrence;
  }

  set payRecurrence(value: PricePeriodicity) {
    this.rawPayRecurrence = value;
    if (this.plan) {
      this.cardPlanName = this.planStateService.determineCardPlanName(this.plan.name, value);
    }
    this.updateBadgeInfo();
  }

  @Input()
  showPopularBadge: boolean;

  @Input()
  useRecommendedBadge = false;

  @Input() isEnterprisePlan = false;

  /** When true, hide per-card upgrade/downgrade buttons (selection handled by parent). */
  @Input() hideActionButtons = false;

  /** When set, use these feature i18n keys instead of plan.featuresSummary. */
  @Input() featuresOverride: string[] | null = null;

  /** When true, hide the “All {tier} features plus” prefix row. */
  @Input() hideAllPlanFeaturesPrefix = false;

  /** When true, hide the popular/recommended badge icon. */
  @Input() hidePopularIcon = false;

  /** When true, hide the “billed annually or $X month-to-month” line under the price. */
  @Input() hideBilledRecurrenceDescription = false;

  /** When true, hide the Active/subscription status badge on the plan header. */
  @Input() hideStatusBadge = false;

  @Output()
  readonly planCardBtnClick = new EventEmitter<PlanCardClickEvent>();

  private rawPayRecurrence: PricePeriodicity;

  @Input() isV2: boolean;
  @Input() userCount: number | null = 1;
  @Input() simpleRecurrenceLabel = false;

  cardPlanName: LegacyPricingPlans;

  readonly badgeInfo$ = new BehaviorSubject<BadgeInfo | null>(null);

  private _cachedFeatures: string[] | null = null;
  private _lastPlanId: string | null = null;
  private _lastPayRecurrence: PricePeriodicity | null = null;
  private _lastFeaturesSummary: string[] | null = null;
  private readonly destroy$ = new Subject<void>();
  private iconRegistered = false;

  get displayedFeatures(): string[] {
    if (this.featuresOverride?.length) {
      return this.featuresOverride;
    }

    const cacheKey = `${this.plan.productId}-${this.payRecurrence}`;
    const lastCacheKey = this._lastPlanId && this._lastPayRecurrence
      ? `${this._lastPlanId}-${this._lastPayRecurrence}`
      : null;

    // Check if plan ID, pay recurrence, or features summary has changed
    const featuresChanged = this._lastFeaturesSummary !== this.plan.featuresSummary;

    if (cacheKey !== lastCacheKey || featuresChanged || !this._cachedFeatures) {
      this._lastPlanId = this.plan.productId;
      this._lastPayRecurrence = this.payRecurrence;
      this._lastFeaturesSummary = this.plan.featuresSummary;

      if (!this.isV2) {
        this._cachedFeatures = this.plan.featuresSummary;
      } else {
        const maxFeatures = this.plan.productId === 'basic' ? 6 : 5;
        this._cachedFeatures = this.plan.featuresSummary.slice(0, maxFeatures);
      }
    }
    return this._cachedFeatures;
  }

  get showFeaturesListTitle(): boolean {
    return !!this.isV2 && !this.featuresOverride?.length;
  }

  get showAllPlanFeaturesPrefix(): boolean {
    return !this.hideAllPlanFeaturesPrefix && this.plan.hasFeaturesInOtherPlan();
  }

  /** Keep price label inline for yearly dialog cards even when not using the short “user” label. */
  get shouldShowInlineRecurrenceLabel(): boolean {
    return this.payRecurrence === 'year' && (this.simpleRecurrenceLabel || this.hideBilledRecurrenceDescription);
  }

  get isCurrentPlan(): boolean {
    return this.planStateService.isCurrentPlan(this.plan, this.currentPlan, this.payRecurrence);
  }

  readonly trialOfferBadge: SubscriptionStatus = { status: 'trial' };

  get isTrialOfferPlan(): boolean {
    return this.trialOffer.isOfferPlan(this.currentPlan?.billingApiData?.billingDetails, this.plan, this.payRecurrence);
  }

  get planDescription(): string {
    return this.planStateService.getPlanDescription(this.plan.name) || '';
  }

  get featuresListTitle(): string {
    return this.planStateService.getFeaturesListTitle(this.plan.name) || '';
  }

  get futureSubscription(): boolean {
    const cardPlanName = this.planStateService.determineCardPlanName(this.plan.name, this.payRecurrence);
    return this.planStateService.isFutureSubscription(this.plan, this.currentPlan, cardPlanName) || false;
  }

  get nextPaymentDateFormatted(): string {
    return this.planStateService.formatNextPaymentDate(this.currentPlan.paymentModel.nextPaymentDate) || '';
  }

  get recurrenceMessage(): string {
    return this.planStateService.getRecurrenceMessage(this.payRecurrence) || '';
  }

  get recurrenceMessageEachPlan(): string {
    return this.planStateService.getRecurrenceMessageEachPlan(this.plan) || '';
  }

  get enterpriseFeatures(): string[] {
    if (!this.isEnterprisePlan) {
      return [];
    }

    const result = this.translate.instant('billing.planFeatureLists.enterprise');

    // Handle object with functions (translation result)
    if (result && typeof result === 'object' && !Array.isArray(result)) {
      return Object.values(result).map(feature =>
        typeof feature === 'function' ? feature() : String(feature),
      );
    }

    // Handle array or other types
    return Array.isArray(result) ? result : [];
  }

  constructor(
    private translate: TranslateService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly planStateService: PlanStateService,
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly trialOffer: TrialOfferService,
    @Inject(USE_CHOOSE_PLAN_INJECTION_TOKEN) @Optional() public useChoosePlan?: boolean,
  ) {
    this.registerCustomIcon();
  }

  ngOnInit() {
    this.updateBadgeInfo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.showPopularBadge ||
      changes.useRecommendedBadge ||
      changes.isV2 ||
      changes.currentPlan ||
      changes.plan
    ) {
      this.updateBadgeInfo();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.badgeInfo$.complete();

    // Clean up cached data to prevent memory leaks
    this._cachedFeatures = null;
    this._lastPlanId = null;
    this._lastPayRecurrence = null;
    this._lastFeaturesSummary = null;
  }

  private registerCustomIcon(): void {
    if (this.iconRegistered) {
      return;
    }

    try {
      this.matIconRegistry.addSvgIcon(
        'popular-icon',
        this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/images/billing/pricing-plan-card/popular-icon.svg'),
      );
      this.iconRegistered = true;
    } catch (error) {
      console.error('Error registering popular icon:', error);
    }
  }

  private updateBadgeInfo(): void {
    const badgeInfo = this.planStateService.calculateBadgeInfo(
      this.showPopularBadge,
      this.isCurrentPlan,
      this.isV2,
      this.useRecommendedBadge,
    );
    this.badgeInfo$.next(badgeInfo);
  }

  getDeterminePlanState(plan: ConcretePlan): () => PlanActionType {
    return this.planStateService.createPlanStateGetter(plan, this.currentPlan, this.payRecurrence) || (() => 'upgrade');
  }

  priceToDisplay(): string {
    // Use localized pricing to handle UnitPriceOverride
    const localizedPriceStr = this.planStateService.getLocalizedPrice(this.plan, this.payRecurrence);

    // Extract numeric value from localized price string (e.g., "$6.70" -> 6.70)
    const numericValue = parseFloat((localizedPriceStr || '0').replace(/[^\d.-]/g, ''));
    const base = normalizePrice(numericValue);

    if (!this.isV2) {
      return base.toString();
    }

    const perUser = Number(base);
    const count = Math.max(1, Number(this.userCount || 1));
    const total = perUser * count;

    // Keep two decimals for total clarity
    return total.toFixed(2);
  }

  public handlePlanCardBtnClick(): void {
    const plan = this.plan.clone();
    plan.switchPlanRecurrence(this.payRecurrence);

    this.planCardBtnClick.emit({
      plan,
      actionType: this.getDeterminePlanState(this.plan)(),
    });
  }

  getTrackingData(action: 'upgrade' | 'downgrade') {
    return this.planStateService.getTrackingData(action, this.currentPlan?.subscription) || {};
  }

  get currencySymbol(): string {
    return this.planStateService.getLocalizedCurrencySymbol(this.plan) || '$';
  }

  get localizedPrice(): string {
    return this.planStateService.getLocalizedPrice(this.plan, this.payRecurrence) || '$0.00';
  }

  fullPriceForTooltip(): string {
    // Get the price without notation formatting for tooltip
    const price = normalizePrice(parseFloat(this.priceToDisplay()));
    const currencySymbol = this.planStateService.getLocalizedCurrencySymbol(this.plan) || '$';
    return formatPriceForTooltip(price, currencySymbol);
  }

  shouldShowTooltip(): boolean {
    // Show tooltip only if the price is >= 1000 (will be formatted with K/M notation)
    const price = normalizePrice(parseFloat(this.priceToDisplay()));
    return price >= 1000;
  }
}

export const USE_CHOOSE_PLAN_INJECTION_TOKEN = new InjectionToken<boolean>('USE_CHOOSE_PLAN_INJECTOR');
